import React, { useState, useEffect } from "react";
import axios ,{AxiosRequestHeaders} from "axios";
import { Link } from "react-router-dom";
import loaderimage from "../../images/giphy.gif";
import ConfirmationModal from "../shared/ConfirmationModal";

interface FAQ {
  faq_id: number;
  question: string;
  answer: string;
  type: "Candidate" | "Employer" | "Admin";
}

const FAQPage = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [filteredFaqs, setFilteredFaqs] = useState<FAQ[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedFaqId, setSelectedFaqId] = useState<number | null>(null);
  const headers: AxiosRequestHeaders = {
    'x-access-token': localStorage.getItem('jwttoken') || '',
  };
  // Fetch FAQs
  const fetchFAQs = async () => {
    try {
      setLoader(true);
      setError(null);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}faqs/all`,{headers});
      setFaqs(response.data);
      setFilteredFaqs(response.data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch FAQs.");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchFAQs();
  }, []);

  // Handle search functionality
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (searchTerm === "") {
        setFilteredFaqs(faqs);
      } else {
        const filtered = faqs.filter(
          (faq) =>
            faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            faq.type.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredFaqs(filtered);
      }
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, faqs]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Delete FAQ
  const deleteFAQ = async (faq_id: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}faqs/delete/${faq_id}`);
      setShowSuccessModal(true);
      fetchFAQs();
    } catch (err: any) {
      setError("Failed to delete FAQ.");
    }
  };

  const handleDeleteClick = (faq_id: number) => {
    setSelectedFaqId(faq_id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = (confirmed: boolean) => {
    if (confirmed && selectedFaqId !== null) {
      deleteFAQ(selectedFaqId);
    }
    setShowDeleteModal(false);
    setSelectedFaqId(null);
  };

  return (
    <>
      <div className="">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="text-black fw-bold">FAQ Management</h2>
          <div className="d-flex gap-2 align-items-center" style={{ width: "510px" }}>
            <span className="fw-bold text-secondary text-nowrap">{filteredFaqs.length} results</span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by question or type"
              value={searchTerm}
              onChange={handleSearch}
            />
            <Link to="/faqadd" className="btn bg-info text-white">
              + Add FAQ
            </Link>
          </div>
        </div>

        <div className="card shadow-sm border-0 rounded">
          <div className="card-header bg-info border-bottom py-3">
            <h5 className="mb-0 text-white">FAQ List</h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light text-center">
                  <tr>
                    <th>#</th>
                    <th>Question</th>
                    <th>Answer</th>
                    <th>Type</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loader ? (
                    <tr>
                      <td colSpan={5} className="text-center py-2">
                        <img src={loaderimage} alt="Loading..." height="40" />
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={5} className="text-center text-danger fw-bold py-3">
                        <div className="alert alert-danger text-center m-3" role="alert">
                          Oops! Something went wrong. Please try again later.
                        </div>
                      </td>
                    </tr>
                  ) : filteredFaqs.length > 0 ? (
                    filteredFaqs.map((faq, index) => (
                      <tr key={faq.faq_id}>
                        <td className="text-center">{index + 1}</td>
                        <td style={{ maxWidth: "300px" }}>
                          <div className="text-center" title={faq.question}>
                            {faq.question}
                          </div>
                        </td>
                        <td style={{ maxWidth: "300px" }}>
                          <div className="text-center" title={faq.answer}>
                            {faq.answer}
                          </div>
                        </td>
                        <td className="text-center">{faq.type}</td>
                        <td>
                          <div className="d-flex justify-content-center gap-2">
                             <Link to={`/faqs/edit/${faq.faq_id}`} className="btn btn-sm btn-outline-warning">
                              Edit
                            </Link> 
                            <button
                              onClick={() => handleDeleteClick(faq.faq_id)}
                              className="btn btn-sm btn-outline-danger"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center text-muted fw-bold">
                        <div className="alert alert-secondary text-center m-3" role="alert">
                          No FAQs available
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          modalMessage="Are you sure you want to delete this FAQ?"
          confirmation={handleDeleteConfirm}
          operationType="Delete"
        />

        {/* Success Modal */}
        <ConfirmationModal
          show={showSuccessModal}
          onHide={() => setShowSuccessModal(false)}
          modalMessage="FAQ deleted successfully."
          operationType="Ok"
        />
      </div>
    </>
  );
};

export default FAQPage;
